// frontend/src/components/employees/WorkExperienceFormModal.js
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Save } from 'lucide-react';
import { endOfMonth, startOfMonth } from 'date-fns';
import Modal from '../UI/Modal';
import ConfirmModal from '../UI/ConfirmModal';
import FormSelect from '../UI/FormSelect';
import DatePickerField from '../UI/DatePickerField';
import SearchableCombobox from '../UI/SearchableCombobox';
import { useCountryOptions } from '../../hooks/useCountryOptions';
import { parseLocalDate, toLocalISODate } from '../../utils/dateUtils';
import {
  WORK_EXP_EMPLOYMENT_TYPE_OPTIONS,
  WORK_EXP_INDUSTRY_OPTIONS,
  WORK_EXP_VERIFICATION_OPTIONS,
} from '../../utils/workExperienceUtils';

const EMPTY_FORM = {
  work_exp_company_name: '',
  work_exp_designation: '',
  work_exp_department: '',
  work_exp_industry: 'construction',
  work_exp_employment_type: 'full_time',
  work_exp_start_date: '',
  work_exp_end_date: '',
  work_exp_is_current: false,
  work_exp_job_description: '',
  work_exp_technologies_used: '',
  work_exp_country: 'Singapore',
  work_exp_location: '',
  work_exp_verification_status: 'pending',
  work_exp_reference_contact: '',
};

const formFromRecord = (record) => {
  if (!record) return { ...EMPTY_FORM };
  return {
    work_exp_company_name: record.work_exp_company_name || '',
    work_exp_designation: record.work_exp_designation || '',
    work_exp_department: record.work_exp_department || '',
    work_exp_industry: record.work_exp_industry || 'construction',
    work_exp_employment_type: record.work_exp_employment_type || 'full_time',
    work_exp_start_date: record.work_exp_start_date || '',
    work_exp_end_date: record.work_exp_end_date || '',
    work_exp_is_current: Boolean(record.work_exp_is_current),
    work_exp_job_description: record.work_exp_job_description || '',
    work_exp_technologies_used: record.work_exp_technologies_used || '',
    work_exp_country: record.work_exp_country || 'Singapore',
    work_exp_location: record.work_exp_location || '',
    work_exp_verification_status: record.work_exp_verification_status || 'pending',
    work_exp_reference_contact: record.work_exp_reference_contact || '',
  };
};

const snapshot = (data) => JSON.stringify(data);

/**
 * Compose modal for adding or editing a work-experience record.
 * Shared by HR Employee Management, CV Data Entry, and employee Profile.
 */
const WorkExperienceFormModal = ({
  isOpen,
  onClose,
  staffId,
  record = null,
  existingRecords = [],
  theme = 'admin',
  showVerification = true,
  isSubmitting = false,
  onSubmit,
}) => {
  const isAdmin = theme === 'admin';
  const isEditing = Boolean(record?.id);
  const { data: countries = [], isLoading: countriesLoading } = useCountryOptions({
    enabled: isOpen,
  });

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [baseline, setBaseline] = useState(snapshot(EMPTY_FORM));
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [showDiscard, setShowDiscard] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const next = formFromRecord(record);
    setFormData(next);
    setBaseline(snapshot(next));
    setDuplicateWarning('');
    setShowDiscard(false);
  }, [isOpen, record]);

  const isDirty = snapshot(formData) !== baseline;
  const inputClass = isAdmin ? 'input-admin w-full' : 'input w-full';
  const textareaClass = isAdmin ? 'textarea-admin w-full' : 'textarea w-full';
  const labelClass = isAdmin ? 'field-label' : 'form-label';
  const focusRing = isAdmin
    ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500'
    : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

  const countryItems = useMemo(() => {
    const names = countries.map((c) => ({ id: c.name, name: c.name }));
    if (formData.work_exp_country && !names.some((c) => c.name === formData.work_exp_country)) {
      names.unshift({ id: formData.work_exp_country, name: formData.work_exp_country });
    }
    return names;
  }, [countries, formData.work_exp_country]);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (duplicateWarning) setDuplicateWarning('');
  };

  const hasOverlap = () => {
    if (!formData.work_exp_start_date) return false;
    const newStart = parseLocalDate(formData.work_exp_start_date);
    const newEnd = formData.work_exp_is_current
      ? new Date()
      : parseLocalDate(formData.work_exp_end_date);
    if (!newStart || !newEnd) return false;

    return existingRecords.some((row) => {
      if (isEditing && row.id === record.id) return false;
      const sameCompany = row.work_exp_company_name?.toLowerCase() === formData.work_exp_company_name.trim().toLowerCase();
      const sameTitle = row.work_exp_designation?.toLowerCase() === formData.work_exp_designation.trim().toLowerCase();
      if (!sameCompany || !sameTitle) return false;
      const existingStart = parseLocalDate(row.work_exp_start_date);
      const existingEnd = row.work_exp_is_current
        ? new Date()
        : parseLocalDate(row.work_exp_end_date);
      if (!existingStart || !existingEnd) return false;
      return newStart <= existingEnd && newEnd >= existingStart;
    });
  };

  const requestClose = () => {
    if (isSubmitting) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onClose();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (hasOverlap()) {
      setDuplicateWarning(
        'A similar record already exists for this company, title, and overlapping dates. Edit that record instead.'
      );
      return;
    }

    const start = parseLocalDate(formData.work_exp_start_date);
    const end = formData.work_exp_is_current ? null : parseLocalDate(formData.work_exp_end_date);
    const payload = {
      work_exp_company_name: formData.work_exp_company_name.trim(),
      work_exp_designation: formData.work_exp_designation.trim(),
      work_exp_department: formData.work_exp_department.trim(),
      work_exp_industry: formData.work_exp_industry,
      work_exp_employment_type: formData.work_exp_employment_type,
      work_exp_start_date: start ? toLocalISODate(startOfMonth(start)) : '',
      work_exp_end_date: end ? toLocalISODate(endOfMonth(end)) : null,
      work_exp_is_current: formData.work_exp_is_current,
      work_exp_job_description: formData.work_exp_job_description.trim(),
      work_exp_technologies_used: formData.work_exp_technologies_used.trim(),
      work_exp_country: formData.work_exp_country || 'Singapore',
      work_exp_location: formData.work_exp_location.trim(),
      work_exp_verification_status: showVerification
        ? formData.work_exp_verification_status
        : (record?.work_exp_verification_status || 'pending'),
      work_exp_reference_contact: formData.work_exp_reference_contact.trim(),
    };
    if (staffId) {
      payload.work_exp_staff_id = staffId;
    }

    onSubmit(payload);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={requestClose}
        title={isEditing ? 'Edit Work Experience' : 'Add Work Experience'}
        size="md"
        theme={theme}
        closable={!isSubmitting}
        footer={(
          <>
            <button
              type="button"
              onClick={requestClose}
              className={isAdmin ? 'btn btn-admin-outline' : 'btn btn-outline'}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="work-experience-form"
              className={isAdmin ? 'btn btn-admin-primary' : 'btn btn-primary'}
              disabled={isSubmitting || Boolean(duplicateWarning)}
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner-sm mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update' : 'Add'} Record
                </>
              )}
            </button>
          </>
        )}
      >
        <form id="work-experience-form" onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">
            Transcribe the role as it appears on the CV or LinkedIn. Month-only dates are stored as the first and last day of those months so duration stays accurate.
          </p>

          {duplicateWarning && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Duplicate detected</p>
                <p className="text-sm text-red-700">{duplicateWarning}</p>
              </div>
            </div>
          )}

          <div>
            <label className={labelClass} htmlFor="work_exp_designation">
              Job title <span className="text-red-500">*</span>
            </label>
            <input
              id="work_exp_designation"
              type="text"
              className={`${inputClass} ${focusRing}`}
              value={formData.work_exp_designation}
              onChange={(e) => updateField('work_exp_designation', e.target.value)}
              placeholder="e.g., BIM Engineer"
              required
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="work_exp_company_name">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              id="work_exp_company_name"
              type="text"
              className={`${inputClass} ${focusRing}`}
              value={formData.work_exp_company_name}
              onChange={(e) => updateField('work_exp_company_name', e.target.value)}
              placeholder="e.g., City Square Super Center"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Employment type <span className="text-red-500">*</span>
              </label>
              <FormSelect
                options={WORK_EXP_EMPLOYMENT_TYPE_OPTIONS}
                value={formData.work_exp_employment_type}
                onChange={(val) => updateField('work_exp_employment_type', val)}
                theme={theme}
                required
              />
            </div>
            <div>
              <label className={labelClass}>
                Industry <span className="text-red-500">*</span>
              </label>
              <FormSelect
                options={WORK_EXP_INDUSTRY_OPTIONS}
                value={formData.work_exp_industry}
                onChange={(val) => updateField('work_exp_industry', val)}
                theme={theme}
                required
              />
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="work_exp_department">
              Department
            </label>
            <input
              id="work_exp_department"
              type="text"
              className={`${inputClass} ${focusRing}`}
              value={formData.work_exp_department}
              onChange={(e) => updateField('work_exp_department', e.target.value)}
              placeholder="e.g., MEP"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Start month <span className="text-red-500">*</span>
              </label>
              <DatePickerField
                selected={parseLocalDate(formData.work_exp_start_date)}
                onChange={(date) => updateField('work_exp_start_date', date ? toLocalISODate(date) : '')}
                className={`${inputClass} pl-10`}
                placeholderText="Select month"
                maxDate={parseLocalDate(formData.work_exp_end_date)}
                showMonthYearPicker
                dateFormat="MMM yyyy"
                theme={theme}
                required
              />
            </div>
            <div>
              <label className={labelClass}>
                End month {!formData.work_exp_is_current && <span className="text-red-500">*</span>}
              </label>
              <DatePickerField
                selected={parseLocalDate(formData.work_exp_end_date)}
                onChange={(date) => updateField('work_exp_end_date', date ? toLocalISODate(date) : '')}
                className={`${inputClass} pl-10`}
                placeholderText="Select month"
                minDate={parseLocalDate(formData.work_exp_start_date)}
                disabled={formData.work_exp_is_current}
                showMonthYearPicker
                dateFormat="MMM yyyy"
                theme={theme}
                isClearable
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="work_exp_is_current"
              className={`w-4 h-4 border-gray-300 rounded ${isAdmin ? 'text-red-600 focus-visible:ring-red-500' : 'text-blue-600 focus-visible:ring-blue-500'}`}
              checked={formData.work_exp_is_current}
              onChange={(e) => updateField('work_exp_is_current', e.target.checked)}
            />
            <label htmlFor="work_exp_is_current" className="text-sm text-gray-700">
              This is a current role
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Country <span className="text-red-500">*</span>
              </label>
              <SearchableCombobox
                variant="form"
                theme={theme}
                items={countryItems}
                value={formData.work_exp_country || ''}
                onChange={(val) => updateField('work_exp_country', val)}
                valueFn={(c) => c.name}
                displayFn={(c) => c.name}
                displayValue={formData.work_exp_country || undefined}
                placeholder="Select country"
                searchPlaceholder="Search country…"
                emptyLabel="No matching country"
                loading={countriesLoading}
                loadingLabel="Loading countries…"
              />
              <p className="form-help">Used for IR21 overseas-work evidence. Do not default this to Singapore for overseas roles.</p>
            </div>
            <div>
              <label className={labelClass} htmlFor="work_exp_location">
                City / region
              </label>
              <input
                id="work_exp_location"
                type="text"
                className={`${inputClass} ${focusRing}`}
                value={formData.work_exp_location}
                onChange={(e) => updateField('work_exp_location', e.target.value)}
                placeholder="e.g., Taunggyi, Shan State"
              />
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="work_exp_job_description">
              Role description
            </label>
            <textarea
              id="work_exp_job_description"
              className={`${textareaClass} ${focusRing}`}
              rows={6}
              value={formData.work_exp_job_description}
              onChange={(e) => updateField('work_exp_job_description', e.target.value)}
              placeholder={"One responsibility per line, as on LinkedIn:\nAssisted in MEP modelling…\nPrepared technical documentation…"}
            />
            <p className="form-help">Paste the LinkedIn bullets as-is. One line per bullet.</p>
          </div>

          <div>
            <label className={labelClass} htmlFor="work_exp_technologies_used">
              Tools & skills
            </label>
            <input
              id="work_exp_technologies_used"
              type="text"
              className={`${inputClass} ${focusRing}`}
              value={formData.work_exp_technologies_used}
              onChange={(e) => updateField('work_exp_technologies_used', e.target.value)}
              placeholder="e.g., Revit, AutoCAD, Document Preparation"
            />
          </div>

          {showVerification && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
              <div>
                <label className={labelClass}>Verification</label>
                <FormSelect
                  options={WORK_EXP_VERIFICATION_OPTIONS}
                  value={formData.work_exp_verification_status}
                  onChange={(val) => updateField('work_exp_verification_status', val)}
                  theme={theme}
                />
                <p className="form-help">LinkedIn transcripts stay pending until a reference check.</p>
              </div>
              <div>
                <label className={labelClass} htmlFor="work_exp_reference_contact">
                  Reference (optional)
                </label>
                <input
                  id="work_exp_reference_contact"
                  type="text"
                  className={`${inputClass} ${focusRing}`}
                  value={formData.work_exp_reference_contact}
                  onChange={(e) => updateField('work_exp_reference_contact', e.target.value)}
                  placeholder="Name or contact"
                />
              </div>
            </div>
          )}
        </form>
      </Modal>

      <ConfirmModal
        isOpen={showDiscard}
        title="Discard unsaved changes?"
        message="This work experience has not been saved. Discard your edits?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        variant="danger"
        theme={theme}
        onConfirm={() => {
          setShowDiscard(false);
          onClose();
        }}
        onCancel={() => setShowDiscard(false)}
      />
    </>
  );
};

export default WorkExperienceFormModal;
